import { LoadingScreen } from "components/LoadingScreen";
import { useEffect, useState } from "react";
import { toastErro } from "components/Toast";
import { useAprovadosPagamento } from "../../../hooks/useAprovadosPagamento";
import { IFiltrosGerenciarPagamento } from "../../../interface/IFiltrosGerenciarPagamento";
import { IDadosPagamento } from "../../../interface/IDadosPagamento";
import { IHistoricoLote } from "../../../interface/IHistoricoLote";
import { ID_FORNECEDOR_TAXA } from "../../../constants/fornecedorTaxa";
import {
  PERMISSOES_GERENCIAR_PAGAMENTOS,
  temPermissao,
} from "../../../constants/permissoes";
import { ISiglaDocumento } from "./constants/siglasDocumento";
import { ModalDetalhesLote } from "../TabAguardandoAprovacao/containers/ModalDetalhesLote";
import { FiltrosAprovados } from "./containers/FiltrosAprovados";
import { ListarAprovados } from "./containers/ListarAprovados";
import { ModalSolicitarCorrecao } from "./containers/ModalSolicitarCorrecao";
import { ModalValidarLote } from "./containers/ModalValidarLote";
import { ModalConfirmarPagamento } from "./containers/ModalConfirmarPagamento";
import { ModalDadosPagamento } from "./containers/ModalDadosPagamento";
import { ModalHistorico } from "./containers/ModalHistorico";
import { ModalAnexarRecibo } from "./containers/ModalAnexarRecibo";
import { ModalSucesso } from "./containers/ModalSucesso";
import { ContainerFiltros } from "./style";

interface IProps {
  aba: string;
  onAtualizarContadores: () => void;
  listaPermissoes: { nome_funcionalidade: string }[];
}

export function TabAprovadosPagamento({
  aba,
  onAtualizarContadores,
  listaPermissoes,
}: IProps) {
  const {
    dados,
    totalRows,
    limit,
    page,
    loading,
    filtro,
    setLimit,
    setPage,
    listar,
    relatorioDetalhado,
    selectedRows,
    setSelectedRows,
    clearRows,
    idLoteEscolhido,
    setIdLoteEscolhido,
    dadosDetalhes,
    setDadosDetalhes,
    buscarDetalhesLote,
    validarLotes,
    solicitarCorrecao,
    confirmarPagamentoFornecedor,
    confirmarPagamentoTaxa,
    buscarSiglasDocumento,
    anexarRecibo,
    buscarDadosPagamento,
    buscarHistorico,
    adicionarObservacao,
    baixarReciboNf,
    baixarComprovante,
    visualizarBoleto,
    baixarBoleto,
    baixarTodosBoletos,
    onClickProcesso,
    pagamentoUrgenteAtivo,
    togglePagamentoUrgente,
    ordenacao,
    ordenar,
  } = useAprovadosPagamento();

  const podeSolicitarCorrecao = temPermissao(
    listaPermissoes,
    PERMISSOES_GERENCIAR_PAGAMENTOS.SOLICITAR_CORRECAO,
  );
  const podeValidar = temPermissao(listaPermissoes, PERMISSOES_GERENCIAR_PAGAMENTOS.VALIDAR);
  const podeConfirmarPagamento = temPermissao(
    listaPermissoes,
    PERMISSOES_GERENCIAR_PAGAMENTOS.CONFIRMAR_PAGAMENTO,
  );

  const [showModalDetalhes, setShowModalDetalhes] = useState(false);
  const [showModalCorrecao, setShowModalCorrecao] = useState(false);
  const [showModalValidar, setShowModalValidar] = useState(false);
  const [showModalConfirmar, setShowModalConfirmar] = useState(false);
  const [showModalDados, setShowModalDados] = useState(false);
  const [showModalHistorico, setShowModalHistorico] = useState(false);
  const [showModalAnexar, setShowModalAnexar] = useState(false);

  const [dadosPagamento, setDadosPagamento] = useState<IDadosPagamento | null>(null);
  const [historico, setHistorico] = useState<IHistoricoLote | null>(null);
  const [idLoteHistorico, setIdLoteHistorico] = useState<number | null>(null);
  const [idLoteAnexar, setIdLoteAnexar] = useState<number | null>(null);
  const [errosConfirmar, setErrosConfirmar] = useState<{ arquivo: string; mensagem: string }[]>([]);
  const [siglasDocumento, setSiglasDocumento] = useState<ISiglaDocumento[]>([]);
  const [idProcessoTaxa, setIdProcessoTaxa] = useState<number | null>(null);

  const [modalSucesso, setModalSucesso] = useState<{
    show: boolean;
    title: string;
    mensagem: string;
    subtexto?: string;
  }>({ show: false, title: "", mensagem: "" });

  useEffect(() => {
    if (aba !== "aprovados") return;
    listar();
  }, [aba, limit, page, filtro, ordenacao]);

  useEffect(() => {
    if (!idLoteEscolhido) return;
    buscarDetalhesLote(idLoteEscolhido)
      .then(({ hasErro }) => {
        if (!hasErro) setShowModalDetalhes(true);
      })
      .catch(() => toastErro("Erro ao buscar detalhes do lote"));
  }, [idLoteEscolhido]);

  const submitFiltro = (dadosFiltro: IFiltrosGerenciarPagamento) => listar(dadosFiltro);
  const handlePageChange = (p: number) => setPage(p);
  const handleLimitChange = (l: number) => setLimit(l);

  const eTaxaSelecionado =
    selectedRows.length > 0 && selectedRows.every((r) => r.idFornecedor === ID_FORNECEDOR_TAXA);
  const umProcessoTaxa =
    eTaxaSelecionado &&
    selectedRows.length === 1 &&
    Number(selectedRows[0]?.qtdProcessos) === 1;
  const umProcessoFornecedor =
    selectedRows.length === 1 &&
    selectedRows[0]?.idFornecedor !== ID_FORNECEDOR_TAXA &&
    Number(selectedRows[0]?.qtdProcessos) === 1;
  const selecaoMistaPagamento =
    selectedRows.some((r) => r.idFornecedor === ID_FORNECEDOR_TAXA) &&
    selectedRows.some((r) => r.idFornecedor !== ID_FORNECEDOR_TAXA);

  const exigeSelecao = (acao: () => void) => {
    if (!selectedRows.length) {
      toastErro("É necessário selecionar um lote ou mais.");
      return;
    }
    acao();
  };

  const onConfirmCorrecao = async (motivo: string, descricao: string) => {
    setShowModalCorrecao(false);
    const { hasErro } = await solicitarCorrecao(motivo, descricao);
    if (!hasErro) {
      setSelectedRows([]);
      setModalSucesso({
        show: true,
        title: "Solicitar correção de lote",
        mensagem: "Solicitação realizada com sucesso!",
        subtexto: 'O lote foi movido para a aba "Reprovados".',
      });
      listar();
      onAtualizarContadores();
    }
  };

  const onConfirmValidar = async (observacao: string) => {
    setShowModalValidar(false);
    const { hasErro } = await validarLotes(observacao);
    if (!hasErro) {
      setSelectedRows([]);
      setModalSucesso({
        show: true,
        title: "Validar lote",
        mensagem: "Lote validado com sucesso!",
      });
      listar();
      onAtualizarContadores();
    }
  };

  const finalizarPagamentoComSucesso = (
    naoSalvos?: { arquivo: string; mensagem: string }[],
  ) => {
    setShowModalConfirmar(false);
    setErrosConfirmar([]);
    setSelectedRows([]);
    setModalSucesso({
      show: true,
      title: "Confirmar pagamento",
      mensagem: "Pagamento confirmado!",
      subtexto: naoSalvos?.length
        ? `${naoSalvos.length} arquivo(s) não foram salvos: ${naoSalvos
            .map((n) => `${n.arquivo} (${n.mensagem})`)
            .join("; ")}`
        : undefined,
    });
    listar();
    onAtualizarContadores();
  };

  const onConfirmPagamentoTaxa = async (dataPagamento: string, arquivos: File[]) => {
    const ids = selectedRows.map((row) => row.id);
    const { hasErro, data } = await confirmarPagamentoTaxa(ids, dataPagamento, arquivos);
    if (hasErro) return;

    if (data?.sucesso) {
      finalizarPagamentoComSucesso(data?.naoSalvos);
      return;
    }

    const erros = [...(data?.erros ?? []), ...(data?.naoSalvos ?? [])];
    if (erros.length) {
      setErrosConfirmar(erros);
    }
  };

  const onConfirmPagamento = async (
    dataPagamento: string,
    arquivos: File[],
    siglaComprovante: string,
  ) => {
    if (eTaxaSelecionado) {
      await onConfirmPagamentoTaxa(dataPagamento, arquivos);
      return;
    }

    const ids = selectedRows.map((row) => row.id);
    const { hasErro, data } = await confirmarPagamentoFornecedor(
      ids,
      dataPagamento,
      arquivos,
      siglaComprovante,
    );
    if (hasErro) return;

    if (data?.erros?.length) {
      setErrosConfirmar(data.erros);
      return;
    }

    if (data?.sucesso) {
      finalizarPagamentoComSucesso();
    }
  };

  const onClickVisualizar = (idLote: number) => setIdLoteEscolhido(idLote);

  const onClickDadosPagamento = async (idLote: number) => {
    const dadosResp = await buscarDadosPagamento(idLote);
    if (dadosResp) {
      setDadosPagamento(dadosResp);
      setShowModalDados(true);
    }
  };

  const abrirHistorico = async (idLote: number) => {
    const resp = await buscarHistorico(idLote);
    if (resp) {
      setHistorico(resp);
      setIdLoteHistorico(idLote);
      setShowModalHistorico(true);
    }
  };

  const onSalvarObservacao = async (observacao: string) => {
    if (!idLoteHistorico) return;
    const { hasErro } = await adicionarObservacao(idLoteHistorico, observacao);
    if (!hasErro) {
      const resp = await buscarHistorico(idLoteHistorico);
      if (resp) setHistorico(resp);
    }
  };

  const onClickAnexarRecibo = (idLote: number) => {
    setIdLoteAnexar(idLote);
    setShowModalAnexar(true);
  };

  const onConfirmAnexarRecibo = async (arquivo: File) => {
    if (!idLoteAnexar) return;
    setShowModalAnexar(false);
    const { hasErro } = await anexarRecibo(idLoteAnexar, arquivo);
    if (!hasErro) {
      setModalSucesso({
        show: true,
        title: "Anexar Recibo / NF",
        mensagem: "Arquivo anexado com sucesso!",
      });
      listar();
    }
  };

  const handleCloseModalDetalhes = () => {
    setShowModalDetalhes(false);
    setDadosDetalhes(null);
    setIdLoteEscolhido(null);
  };

  return (
    <ContainerFiltros>
      <LoadingScreen mostrar={loading} />
      <FiltrosAprovados submitForm={submitFiltro} />
      <ListarAprovados
        dados={dados}
        handleLimitChange={handleLimitChange}
        handlePageChange={handlePageChange}
        totalRows={totalRows}
        setRowsSelected={setSelectedRows}
        selectedRows={selectedRows}
        linhasPorPagina={limit}
        clearRows={clearRows}
        pagamentoUrgenteAtivo={pagamentoUrgenteAtivo}
        onTogglePagamentoUrgente={togglePagamentoUrgente}
        onClickSolicitarCorrecao={() => exigeSelecao(() => setShowModalCorrecao(true))}
        onClickValidar={() => exigeSelecao(() => setShowModalValidar(true))}
        onClickConfirmarPagamento={() =>
          exigeSelecao(async () => {
            setErrosConfirmar([]);
            setIdProcessoTaxa(null);
            if (eTaxaSelecionado || umProcessoFornecedor) {
              setSiglasDocumento(await buscarSiglasDocumento());
            }
            if (umProcessoTaxa) {
              const { data } = await buscarDetalhesLote(selectedRows[0].id);
              setIdProcessoTaxa(data?.processos?.[0]?.idProcesso ?? null);
            }
            setShowModalConfirmar(true);
          })
        }
        onClickVisualizar={onClickVisualizar}
        onClickDadosPagamento={onClickDadosPagamento}
        onClickComprovante={baixarComprovante}
        onClickHistorico={abrirHistorico}
        onClickAnexarRecibo={onClickAnexarRecibo}
        onBaixarRecibo={baixarReciboNf}
        onOrdenar={ordenar}
        relatorioDetalhado={relatorioDetalhado}
        podeSolicitarCorrecao={podeSolicitarCorrecao}
        podeValidar={podeValidar}
        podeConfirmarPagamento={podeConfirmarPagamento}
      />

      <ModalDetalhesLote
        show={showModalDetalhes}
        dados={dadosDetalhes}
        handleClose={handleCloseModalDetalhes}
        onClickProcesso={onClickProcesso}
        onBaixarRecibo={(idLote) =>
          baixarReciboNf(idLote, dadosDetalhes?.numeroNfRecibo ?? String(idLote))
        }
      />

      <ModalSolicitarCorrecao
        show={showModalCorrecao}
        handleClose={() => setShowModalCorrecao(false)}
        onConfirm={onConfirmCorrecao}
      />

      <ModalValidarLote
        show={showModalValidar}
        handleClose={() => setShowModalValidar(false)}
        onConfirm={onConfirmValidar}
      />

      <ModalConfirmarPagamento
        show={showModalConfirmar}
        eTaxa={eTaxaSelecionado}
        umProcessoTaxa={umProcessoTaxa}
        umProcessoFornecedor={umProcessoFornecedor}
        idProcesso={idProcessoTaxa}
        selecaoMista={selecaoMistaPagamento}
        siglas={siglasDocumento}
        erros={errosConfirmar}
        handleClose={() => {
          setShowModalConfirmar(false);
          setErrosConfirmar([]);
        }}
        onConfirm={onConfirmPagamento}
        onRemoverErro={(arquivo) =>
          setErrosConfirmar((prev) => prev.filter((e) => e.arquivo !== arquivo))
        }
      />

      <ModalDadosPagamento
        show={showModalDados}
        dados={dadosPagamento}
        onVisualizarBoleto={visualizarBoleto}
        onBaixarBoleto={baixarBoleto}
        onBaixarTodosBoletos={baixarTodosBoletos}
        handleClose={() => {
          setShowModalDados(false);
          setDadosPagamento(null);
        }}
      />

      <ModalHistorico
        show={showModalHistorico}
        idLote={idLoteHistorico}
        dados={historico}
        handleClose={() => {
          setShowModalHistorico(false);
          setHistorico(null);
          setIdLoteHistorico(null);
        }}
        onSalvarObservacao={onSalvarObservacao}
      />

      <ModalAnexarRecibo
        show={showModalAnexar}
        handleClose={() => {
          setShowModalAnexar(false);
          setIdLoteAnexar(null);
        }}
        onConfirm={onConfirmAnexarRecibo}
      />

      <ModalSucesso
        show={modalSucesso.show}
        title={modalSucesso.title}
        mensagem={modalSucesso.mensagem}
        subtexto={modalSucesso.subtexto}
        handleClose={() => setModalSucesso((prev) => ({ ...prev, show: false }))}
      />
    </ContainerFiltros>
  );
}
